import { createReducer, on } from '@ngrx/store';

import { NotificationItem } from '@shared/models/in-app-notification.model';
import { PaginationResponse } from '@shared/models/common.model';
import {
  findNotificationItemByKey,
  isSameNotificationEvent,
} from '@shared/utils/build-notification';
import * as NotificationsActions from './notifications.actions';

export const NOTIFICATIONS_FEATURE_KEY = 'notifications';

export interface NotificationsState {
  items: NotificationItem[];
  unreadCount: number;
  pagination: PaginationResponse | null;
  hasMore: boolean;
  /** True once the first REST hydrate has settled this session. */
  hydrated: boolean;
  /** Initial / replace load in flight. */
  loading: boolean;
  /** Append (infinite-scroll) load in flight. */
  loadingMore: boolean;
}

export const initialNotificationsState: NotificationsState = {
  items: [],
  unreadCount: 0,
  pagination: null,
  hasMore: false,
  hydrated: false,
  loading: false,
  loadingMore: false,
};

function isDuplicateById(
  existing: NotificationItem,
  incoming: NotificationItem
): boolean {
  if (existing.id === incoming.id) {
    return true;
  }
  if (
    existing.notificationId &&
    incoming.notificationId &&
    existing.notificationId === incoming.notificationId
  ) {
    return true;
  }
  return false;
}

function isDuplicate(
  existing: NotificationItem,
  incoming: NotificationItem
): boolean {
  if (isDuplicateById(existing, incoming)) {
    return true;
  }
  return isSameNotificationEvent(existing, incoming);
}

function hasMorePages(pagination: PaginationResponse | null): boolean {
  return !!pagination && pagination.page < pagination.totalPages;
}

export const notificationsReducer = createReducer(
  initialNotificationsState,

  on(NotificationsActions.loadNotifications, (state, { append = false }) => ({
    ...state,
    loading: append ? state.loading : true,
    loadingMore: append,
  })),

  on(
    NotificationsActions.loadNotificationsSuccess,
    (state, { items, pagination, append, unreadCount }) => {
      let nextItems: NotificationItem[];

      if (append) {
        const fresh = items.filter(
          incoming =>
            !state.items.some(existing => isDuplicateById(existing, incoming))
        );
        nextItems = [...state.items, ...fresh];
      } else {
        const liveOnly = state.items.filter(
          live =>
            !items.some(
              persisted =>
                (live.notificationId &&
                  persisted.notificationId &&
                  live.notificationId === persisted.notificationId) ||
                isSameNotificationEvent(live, persisted)
            )
        );
        const mergedPersisted = items.map(persisted => {
          const liveMatch = state.items.find(
            live =>
              (live.notificationId &&
                persisted.notificationId &&
                live.notificationId === persisted.notificationId) ||
              isSameNotificationEvent(live, persisted)
          );
          if (liveMatch?.read && !persisted.read) {
            return { ...persisted, read: true };
          }
          return persisted;
        });
        nextItems = [...liveOnly, ...mergedPersisted];
      }

      return {
        ...state,
        items: nextItems,
        pagination,
        hasMore: hasMorePages(pagination),
        unreadCount:
          unreadCount !== undefined
            ? Math.max(0, unreadCount)
            : state.unreadCount,
        hydrated: true,
        loading: false,
        loadingMore: false,
      };
    }
  ),

  on(NotificationsActions.loadNotificationsFailure, state => ({
    ...state,
    hydrated: true,
    loading: false,
    loadingMore: false,
  })),

  on(NotificationsActions.notificationReceived, (state, { item }) => {
    if (state.items.some(existing => isDuplicate(existing, item))) {
      return state;
    }
    return {
      ...state,
      items: [item, ...state.items],
      unreadCount: state.unreadCount + (item.read ? 0 : 1),
    };
  }),

  on(NotificationsActions.markNotificationRead, (state, { id }) => {
    const target = findNotificationItemByKey(state.items, id);
    if (!target || target.read) {
      return state;
    }
    const items = state.items.map(item =>
      item.clientKey === target.clientKey ? { ...item, read: true } : item
    );
    return {
      ...state,
      items,
      unreadCount: Math.max(0, state.unreadCount - 1),
    };
  }),

  on(NotificationsActions.markNotificationReadFailure, (state, { id }) => {
    const target = findNotificationItemByKey(state.items, id);
    if (!target?.read) {
      return state;
    }
    const items = state.items.map(item =>
      item.clientKey === target.clientKey ? { ...item, read: false } : item
    );
    return {
      ...state,
      items,
      unreadCount: state.unreadCount + 1,
    };
  }),

  on(NotificationsActions.markAllNotificationsRead, state => ({
    ...state,
    items: state.items.map(item =>
      item.read ? item : { ...item, read: true }
    ),
    unreadCount: 0,
  })),

  on(NotificationsActions.clearNotifications, state => ({
    ...initialNotificationsState,
    // Keep hydrated so login hydrate does not immediately re-fill the panel
    // after an explicit clear in the same session.
    hydrated: state.hydrated,
  }))
  // Signing out resets this slice with every other one, through the root
  // `clearStateOnSignOut` meta-reducer (F-031) — `hydrated` included, so the
  // next operator's login re-hydrates.
);
